import { useState } from 'react'

import type { SaldoDeSobrante } from '@/lib/supabase/tipos-bd'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'

import { ElegirOrden } from './ElegirOrden'
import { cantidadLegible, leerCantidad, useAsignarSobrantesAOrden } from './useSobrantes'
import estilos from './PaginaSobrantes.module.css'

type Props = { saldos: SaldoDeSobrante[]; onListo: (texto: string) => void; onCancelar: () => void }

/**
 * ASIGNAR SOBRANTES A UNA ORDEN DE TRABAJO (0314).
 *
 * Johan: «saldrán de inventario de sobrantes y se pueden asociar a una ODT
 * (un material o varios)». Se elige la orden, se escribe cuánto va de cada
 * material y se asigna todo de una vez: si de uno no alcanza, no sale
 * ninguno. No toca la cotización ni la bodega.
 */
export function AsignarAOrden({ saldos, onListo, onCancelar }: Props) {
  const asignar = useAsignarSobrantesAOrden()
  const [orden, setOrden] = useState('')
  const [cantidades, setCantidades] = useState<Record<number, string>>({})
  const [nota, setNota] = useState('')
  const [buscar, setBuscar] = useState('')
  const [error, setError] = useState<string | null>(null)

  const conSaldo = saldos.filter((s) => s.saldo > 0)
  const marcados = conSaldo
    .map((s) => ({ s, n: leerCantidad(cantidades[s.id_inventario] ?? '') }))
    .filter((x): x is { s: SaldoDeSobrante; n: number } => x.n !== null && x.n > 0)
  const pasados = marcados.filter((x) => x.n > x.s.saldo)
  const lista = orden !== '' && marcados.length > 0 && pasados.length === 0

  const t = buscar.trim().toLowerCase()
  const visibles = t ? conSaldo.filter((s) => s.descripcion.toLowerCase().includes(t)) : conSaldo

  async function guardar() {
    if (!lista) return
    setError(null)
    try {
      await asignar.mutateAsync({
        ordenTrabajoId: orden,
        items: marcados.map((x) => ({ id_inventario: x.s.id_inventario, cantidad: x.n })),
        nota: nota.trim() || null,
      })
      onListo(
        `${marcados.length} material${marcados.length === 1 ? '' : 'es'} del inventario de sobrantes quedaron asignados a la orden.`,
      )
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo asignar.')
    }
  }

  return (
    <section className={estilos.asignar} aria-label="Asignar sobrantes a una orden">
      <header className={estilos.asignarCabecera}>
        <div>
          <p className={estilos.salidaTitulo}>Asignar a una orden de trabajo</p>
          <p className={estilos.salidaNota}>
            Sale del inventario de sobrantes, no de bodega, y no cambia ninguna cotización.
          </p>
        </div>
        <ElegirOrden valor={orden} onCambio={setOrden} />
      </header>

      {conSaldo.length > 6 && (
        <input
          className={estilos.buscador}
          type="search"
          placeholder="Buscar material"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          aria-label="Buscar material"
        />
      )}

      <ul className={estilos.asignarLista}>
        {visibles.map((s) => {
          const { codigo, nombre } = partirDescripcion(s.descripcion)
          const n = leerCantidad(cantidades[s.id_inventario] ?? '')
          const pasado = n !== null && n > s.saldo
          return (
            <li key={s.id_inventario} className={n && n > 0 ? estilos.asignarMarcado : undefined}>
              <span className={estilos.renglonMaterial}>
                <span className={estilos.nombre}>{nombre}</span>
                <span className={estilos.detalle}>
                  {codigo && <span className={estilos.codigo}>{codigo}</span>}
                  Hay <strong>{cantidadLegible(s.saldo)}</strong> {s.unidad ?? ''}
                </span>
                {pasado && <span className={estilos.error}>Solo hay {cantidadLegible(s.saldo)}.</span>}
              </span>
              <span className={estilos.asignarCantidad}>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={cantidades[s.id_inventario] ?? ''}
                  aria-invalid={pasado}
                  aria-label={`Cuánto va de ${nombre}`}
                  onChange={(e) => setCantidades((c) => ({ ...c, [s.id_inventario]: e.target.value }))}
                />
                <button
                  type="button"
                  className={estilos.botonTexto}
                  onClick={() =>
                    setCantidades((c) => ({
                      ...c,
                      [s.id_inventario]: s.saldo.toLocaleString('es-CO', { useGrouping: false }),
                    }))
                  }
                >
                  Todo
                </button>
              </span>
            </li>
          )
        })}
      </ul>

      <label className={estilos.campoLargo}>
        <span>Nota (opcional)</span>
        <input type="text" value={nota} onChange={(e) => setNota(e.target.value)} />
      </label>

      {error && <p className={estilos.error}>{error}</p>}
      <div className={estilos.salidaBotones}>
        <span className={estilos.asignarResumen}>
          {marcados.length === 0
            ? 'Escriba cuánto va de cada material.'
            : `${marcados.length} material${marcados.length === 1 ? '' : 'es'} para la orden.`}
        </span>
        <button type="button" className={estilos.botonTexto} onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          className={estilos.botonFuerte}
          disabled={!lista || asignar.isPending}
          onClick={() => void guardar()}
        >
          {asignar.isPending ? 'Asignando…' : 'Asignar a la orden'}
        </button>
      </div>
    </section>
  )
}
